import { useEffect, useMemo, useRef, useState } from 'react';

/** A select you can type into, with what you picked shown as tags.
 *
 *  This is the control Drupal had through Chosen, and it is the right one for
 *  the lists this app has: 33 clients, 289 projects, 62 people. A native
 *  select makes you scroll for the one you want, and a wall of every option as
 *  a chip is worse still — it was 33 outlined boxes taking a third of the
 *  form to say which two were on.
 *
 *  Keyboard: type to filter, arrows to move, Enter to take, Escape to close,
 *  Backspace on an empty box to drop the last tag. */

export interface Option {
  value: number;
  label: string;
  hint?: string;
}

interface Base {
  id: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
}

type Props =
  | (Base & { multiple: true; value: number[]; onChange: (next: number[]) => void })
  | (Base & { multiple?: false; value: number | undefined; onChange: (next: number | undefined) => void });

export function TagSelect(props: Props) {
  const { id, options, placeholder, disabled } = props;
  const multiple = props.multiple === true;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const chosen: number[] = multiple
    ? (props.value as number[])
    : props.value === undefined ? [] : [props.value as number];

  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return options
      .filter((o) => !multiple || !chosen.includes(o.value))
      .filter((o) => needle === '' ||
        o.label.toLowerCase().includes(needle) ||
        (o.hint ?? '').toLowerCase().includes(needle))
      .slice(0, 50);
  }, [options, query, chosen, multiple]);

  useEffect(() => setHighlight(0), [query, open]);

  // Clicking away closes it, which is what everybody expects of a popup.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const take = (option: Option) => {
    if (multiple) {
      props.onChange([...(props.value as number[]), option.value]);
      setQuery('');
      input.current?.focus();
    } else {
      props.onChange(option.value);
      setQuery('');
      setOpen(false);
    }
  };

  const drop = (value: number) => {
    if (multiple) props.onChange((props.value as number[]).filter((v) => v !== value));
    else props.onChange(undefined);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => {
        const next = e.key === 'ArrowDown' ? h + 1 : h - 1;
        return Math.max(0, Math.min(next, matches.length - 1));
      });
      return;
    }
    if (e.key === 'Enter' && open && matches[highlight]) {
      e.preventDefault();
      take(matches[highlight]);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'Backspace' && query === '' && chosen.length > 0) {
      drop(chosen[chosen.length - 1]!);
    }
  };

  return (
    <div className={['tagselect', multiple && 'multi', open && 'open'].filter(Boolean).join(' ')} ref={box}>
      <div
        className="control"
        onClick={() => { if (!disabled) { setOpen(true); input.current?.focus(); } }}
      >
        {chosen.map((v) => {
          const option = byValue.get(v);
          if (!option) return null;
          return (
            <span className="tag" key={v} title={option.label}>
              <span className="label">{option.label}</span>
              <button
                type="button"
                aria-label={`${option.label} ×`}
                onClick={(e) => { e.stopPropagation(); drop(v); }}
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          id={id}
          ref={input}
          className="typed"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          autoComplete="off"
          disabled={disabled}
          placeholder={chosen.length === 0 ? placeholder : ''}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
        />
      </div>

      {open && (
        <ul className="options" id={`${id}-list`} role="listbox">
          {matches.length === 0 && <li className="none">—</li>}
          {matches.map((o, i) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={i === highlight ? 'on' : undefined}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => take(o)}
              >
                {o.label}
                {o.hint && <span className="hint"> {o.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
